import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { HookhouseFormData, DEFAULT_STRUCTURE_CONFIG, DEFAULT_RHYME_CONFIG } from '@/types';
import { DEFAULT_FORM_VALUES } from '@/constants';
import { useUndoStack } from '@hooks/useUndoStack';
import type { NarrativeBlueprint } from '@/types/narrative.types';

// ============================================================================
// TYPES
// ============================================================================

interface StudioFormContextValue {
  formData: HookhouseFormData;
  setFormData: React.Dispatch<React.SetStateAction<HookhouseFormData>>;
  updateField: <K extends keyof HookhouseFormData>(field: K, value: HookhouseFormData[K]) => void;
  resetForm: () => void;
  loadFromRiffWriter: (riffWriterData: RiffWriterSeedData) => void;
  loadFormData: (data: Partial<HookhouseFormData>) => void;

  // Convenience getters/setters for commonly accessed fields
  seed: string;
  setSeed: (seed: string) => void;
  stylePreset: string;
  setStylePreset: (preset: string) => void;
  blends: string[];
  setBlends: (blends: string[]) => void;
  toggleBlend: (blend: string) => void;
  mood: string[];
  setMood: (mood: string[]) => void;
  toggleMood: (mood: string) => void;
  explicit: boolean;
  setExplicit: (explicit: boolean) => void;

  // Narrative Engine
  narrativeBlueprint: NarrativeBlueprint | null;
  setNarrativeBlueprint: (bp: NarrativeBlueprint | null) => void;

  // Persona
  activePersonaId: string | null;
  setActivePersonaId: (id: string | null) => void;

  // Undo/Redo
  undoForm: () => void;
  redoForm: () => void;
  canUndoForm: boolean;
  canRedoForm: boolean;
}

// RiffWriter data structure for import
export interface RiffWriterSeedData {
  title: string;
  concept: string;
  povSetting: string;
  mustHaves: string[];
  avoid: string[];
  instruments: string[];
  structure: string;
  bpm: number;
  key: string;
}

const StudioFormContext = createContext<StudioFormContextValue | undefined>(undefined);

// ============================================================================
// STORAGE KEY
// ============================================================================

const STORAGE_KEY = 'hookhouse_studio_form';

// ============================================================================
// PROVIDER
// ============================================================================

interface StudioFormProviderProps {
  children: ReactNode;
}

export const StudioFormProvider: React.FC<StudioFormProviderProps> = ({ children }) => {
  // Initialize from localStorage or defaults
  const [formData, setFormData] = useState<HookhouseFormData>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Merge with defaults to handle any new fields added in updates. A
        // shallow spread alone isn't enough for songStructure/rhymeScheme:
        // they used to be plain strings (pre-taxonomy-redesign) and browsers
        // with that shape still in localStorage crash the whole app on load
        // -- MultiToggle reads structure.modifiers/rhyme.devices expecting
        // arrays, and a leftover string has neither, so .includes() throws
        // on `undefined`. Reject anything that isn't already the current
        // object shape rather than trusting whatever was persisted.
        const merged = { ...DEFAULT_FORM_VALUES, ...parsed };
        if (!Array.isArray(merged.songStructure?.modifiers)) {
          merged.songStructure = DEFAULT_STRUCTURE_CONFIG;
        }
        if (!Array.isArray(merged.rhymeScheme?.devices)) {
          merged.rhymeScheme = DEFAULT_RHYME_CONFIG;
        }
        return merged;
      }
    } catch (e) {
      console.error('Failed to load studio form from localStorage:', e);
    }
    return DEFAULT_FORM_VALUES;
  });

  // Undo/Redo
  const formUndo = useUndoStack<HookhouseFormData>({ debounceMs: 500, maxHistory: 30 });

  useEffect(() => {
    formUndo.pushState(formData);
  }, [formData]);

  const undoForm = useCallback(() => {
    const state = formUndo.undo();
    if (state) setFormData(state);
  }, [formUndo]);

  const redoForm = useCallback(() => {
    const state = formUndo.redo();
    if (state) setFormData(state);
  }, [formUndo]);

  // Narrative Engine
  const [narrativeBlueprint, setNarrativeBlueprint] = useState<NarrativeBlueprint | null>(null);

  // Persona
  const [activePersonaId, setActivePersonaIdState] = useState<string | null>(() => {
    try {
      return localStorage.getItem('hookhouse_active_persona_id') || null;
    } catch { return null; }
  });
  const setActivePersonaId = useCallback((id: string | null) => {
    setActivePersonaIdState(id);
    try {
      if (id) localStorage.setItem('hookhouse_active_persona_id', id);
      else localStorage.removeItem('hookhouse_active_persona_id');
    } catch { /* noop */ }
  }, []);

  // Persist to localStorage on changes
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(formData));
    } catch (e) {
      console.error('Failed to save studio form to localStorage:', e);
    }
  }, [formData]);

  // Generic field updater
  const updateField = useCallback(<K extends keyof HookhouseFormData>(
    field: K, 
    value: HookhouseFormData[K]
  ) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  // Reset to defaults
  const resetForm = useCallback(() => {
    setFormData(DEFAULT_FORM_VALUES);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  // ----- Convenience Methods -----

  const setSeed = useCallback((seed: string) => {
    updateField('seed', seed);
  }, [updateField]);

  const setStylePreset = useCallback((preset: string) => {
    updateField('stylePreset', preset);
  }, [updateField]);

  const setBlends = useCallback((blends: string[]) => {
    updateField('blends', blends);
  }, [updateField]);

  const toggleBlend = useCallback((blend: string) => {
    setFormData(prev => {
      if (prev.blends.includes(blend)) {
        return { ...prev, blends: prev.blends.filter(b => b !== blend) };
      } else {
        if (prev.blends.length >= 3) return prev;
        return { ...prev, blends: [...prev.blends, blend] };
      }
    });
  }, []);

  const setMood = useCallback((mood: string[]) => {
    updateField('mood', mood);
  }, [updateField]);

  const toggleMood = useCallback((mood: string) => {
    setFormData(prev => {
      if (prev.mood.includes(mood)) {
        return { ...prev, mood: prev.mood.filter(m => m !== mood) };
      } else {
        if (prev.mood.length >= 4) return prev;
        return { ...prev, mood: [...prev.mood, mood] };
      }
    });
  }, []);

  const setExplicit = useCallback((explicit: boolean) => {
    updateField('explicit', explicit);
  }, [updateField]);

  const loadFormData = useCallback((data: Partial<HookhouseFormData>) => {
    setFormData(prev => {
      // Same stale-shape risk as the localStorage load above: callers here
      // include restoring an old SessionMemory snapshot, whose formData may
      // predate the songStructure/rhymeScheme object redesign.
      const merged = { ...DEFAULT_FORM_VALUES, ...prev, ...data };
      if (!Array.isArray(merged.songStructure?.modifiers)) {
        merged.songStructure = DEFAULT_STRUCTURE_CONFIG;
      }
      if (!Array.isArray(merged.rhymeScheme?.devices)) {
        merged.rhymeScheme = DEFAULT_RHYME_CONFIG;
      }
      return merged;
    });
  }, []);

  const loadFromRiffWriter = useCallback((riffWriterData: RiffWriterSeedData) => {
    setFormData(prev => ({
      ...prev,
      seed: `Title: ${riffWriterData.title} - Concept: ${riffWriterData.concept}`,
      povSetting: riffWriterData.povSetting,
      themesToHit: riffWriterData.mustHaves.join(', '),
      themesToAvoid: riffWriterData.avoid.join(', '),
      instruments: riffWriterData.instruments.join(', '),
      constraints: `${riffWriterData.structure}, ${riffWriterData.bpm} BPM, ${riffWriterData.key}`
    }));
  }, []);

  const value: StudioFormContextValue = {
    formData,
    setFormData,
    updateField,
    resetForm,
    loadFromRiffWriter,
    loadFormData,

    // Convenience
    seed: formData.seed,
    setSeed,
    stylePreset: formData.stylePreset,
    setStylePreset,
    blends: formData.blends,
    setBlends,
    toggleBlend,
    mood: formData.mood,
    setMood,
    toggleMood,
    explicit: formData.explicit,
    setExplicit,

    // Narrative Engine
    narrativeBlueprint,
    setNarrativeBlueprint,

    // Persona
    activePersonaId,
    setActivePersonaId,

    // Undo/Redo
    undoForm,
    redoForm,
    canUndoForm: formUndo.canUndo,
    canRedoForm: formUndo.canRedo,
  };

  return (
    <StudioFormContext.Provider value={value}>
      {children}
    </StudioFormContext.Provider>
  );
};

// ============================================================================
// HOOK
// ============================================================================

export const useStudioForm = (): StudioFormContextValue => {
  const context = useContext(StudioFormContext);
  if (!context) {
    throw new Error('useStudioForm must be used within a StudioFormProvider');
  }
  return context;
};